import type * as types from "../../../types/types";
import { useEffect, useRef, useState } from "react";
import {
  PNL_COLORS,
  ROW_PADDING,
  getIsSafari,
} from "../../../constants/constants";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import { mapPnlCategory } from "utils/pnlMapping";
import * as d3 from "d3";
import { useTimelineTooltipStore } from "./TimelineTooltip/timelineTooltipStore";
import { camelCase, isEqual } from "lodash";
import { unselectAllSegments } from "./ProductionSegments/MouseMoveHandler";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useDataSourceGroups } from "../HubletView/SingleTimeline/singleTimelineStore";
import styled, { css } from "styled-components/macro";
import { hexToRgb } from "@mui/material";
import { TimeRangeFinite } from "reports/types";
import { PnlCategoriesEnum } from "types/enums";
const isSafari = getIsSafari();

const OUTLINE_WIDTH = 2;

export function CheapCanvasProductionSegments({
  processId,
  segments,
  height,
  width,
  timelineIdx,
}: {
  processId: string;
  segments: types.ProductionSegmentType[];
  height: number;
  width: number;
  timelineIdx: number;
}) {
  const timelineId = `timeline-canvas-${timelineIdx}-${processId}`;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // when a tag is hovered in another chart via highlightTags function, highlight the corresponding segments (see `CustomEvent("HighlightSegments"` in pnlSummaryChartUtils.ts)
  const [hoveredTag, setHoveredTag] = useState({
    camelCaseTag: "",
    rangeToHighlight: null,
    isOverall: false,
  });
  // listen for event "HighlightSegments" to highlight segments
  useEffect(() => {
    const handleHighlightSegments = (event: CustomEvent) => {
      const { camelCaseTag, doHighlight, rangeToHighlight, isOverall } =
        event.detail;
      if (doHighlight) {
        setHoveredTag({ camelCaseTag, rangeToHighlight, isOverall });
      } else {
        setHoveredTag({
          camelCaseTag: "",
          rangeToHighlight: null,
          isOverall: false,
        });
      }
    };
    document.addEventListener<any>(
      "HighlightSegments",
      handleHighlightSegments
    );
    return () => {
      document.removeEventListener<any>(
        "HighlightSegments",
        handleHighlightSegments
      );
    };
  }, []);

  const { xLeft, xRight, scaleTime } = useTimeAxisContext();
  const [{ isModalPinned, selectedProdSegment }, setTimelineTooltipState] =
    useTimelineTooltipStore();
  const [
    {
      general: { timelineShrinkSegmentCategories: categoriesToShrink },
    },
  ] = useFlightConfig();
  const { selectedDataSourceGroups } = useDataSourceGroups();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext) {
      console.log("No canvas found");
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      console.log("Failed to get 2D context");
      return;
    }

    context.clearRect(0, 0, width, height);

    segments.forEach(segment => {
      // Nulls on the range mean the segment extends forever in either direction
      const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : xLeft;
      const isCurrentSegment = !segment.range.lt;
      const x1 =
        scaleTime(
          isCurrentSegment ? Date.now() : (segment.range.lt as number)
        ) || 0;

      // Check if no part of the segment will be displayed
      const isSegmentOutsideOfFocusRange =
        x0 >= x1 || x0 >= xRight || x1 < xLeft;
      if (isSegmentOutsideOfFocusRange) {
        return null;
      }

      const pnlCategory = segment?.pnl?.category || "unknown";

      const mappedPnlCategory = mapPnlCategory(pnlCategory);
      const rectRight = Math.min(x1, xRight);
      const rectLeft = Math.max(x0, xLeft);
      const rectWidth = rectRight - rectLeft;
      const fillColor = PNL_COLORS[mappedPnlCategory];
      if (rectWidth <= 0 || isNaN(rectLeft) || !fillColor) {
        return null;
      }

      const x = rectLeft;
      let y = 0;
      const segmentWidth = rectWidth;
      let segmentHeight = height;

      if (categoriesToShrink.includes(mappedPnlCategory)) {
        segmentHeight = height * 0.2;
        y = (height - segmentHeight) / 2;
      }

      context.fillStyle = fillColor;
      // if hoveredTag matches the segment, highlight the segment
      const matchesTag =
        segment.tag?.name &&
        hoveredTag.camelCaseTag === camelCase(segment.tag.name);
      const matchesPnlCategory =
        (segment.pnl?.category &&
          hoveredTag.isOverall &&
          hoveredTag.camelCaseTag === camelCase(segment.pnl.category)) ||
        // exceptions for producing, unknownStopTime, noProductionScheduled, uncollected
        (hoveredTag.camelCaseTag === "producing" &&
          segment.pnl.category === PnlCategoriesEnum.producing) ||
        (hoveredTag.camelCaseTag === "unknownStopTime" &&
          segment.pnl.category === PnlCategoriesEnum.unknown) ||
        (hoveredTag.camelCaseTag === "noProductionScheduled" &&
          segment.pnl.category === PnlCategoriesEnum.notScheduled) ||
        (hoveredTag.camelCaseTag === "uncollected" &&
          segment.pnl.category === PnlCategoriesEnum.uncollected);
      if (matchesTag || matchesPnlCategory) {
        const isHighlighted =
          segment.range &&
          (!hoveredTag.rangeToHighlight ||
            getIsRangeWithinHighlightRange(
              {
                ge: segment.range.ge ?? 0,
                lt: segment.range.lt ?? 0,
              },
              hoveredTag.rangeToHighlight
            ));
        if (isHighlighted) {
          const fillColorWithOpacity = hexToRgb(fillColor).replace(
            ")",
            ", 0.5)"
          );
          context.fillStyle = fillColorWithOpacity;
        } else {
          context.fillStyle = fillColor;
        }
      }
      context.fillRect(x, y, segmentWidth, segmentHeight);

      const isSelected = isEqual(selectedProdSegment, segment);
      if (isSelected) {
        // draw a 2px wide inset outline box around the inside of the segment
        context.strokeStyle = "black";
        context.lineWidth = OUTLINE_WIDTH;
        context.strokeRect(
          x + OUTLINE_WIDTH / 2,
          y + OUTLINE_WIDTH / 2,
          segmentWidth - OUTLINE_WIDTH,
          segmentHeight - OUTLINE_WIDTH
        );
      }

      return null;
    });
  }, [
    segments,
    height,
    width,
    timelineId,
    xLeft,
    xRight,
    scaleTime,
    selectedProdSegment,
    isModalPinned,
    categoriesToShrink,
    selectedDataSourceGroups,
    hoveredTag,
  ]);

  return (
    <StyledForeignObject
      width={width}
      height={height}
      $translateYSafari={getTranslateYSafari(timelineIdx, height)}
    >
      <div
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
        onMouseMove={e => {
          const context = canvasRef.current?.getContext("2d");
          if (!context || isModalPinned) {
            return null;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX ? e.clientX - rect.left : 0;
          const segment = findSegment(segments, scaleTime, x);
          if (segment) {
            const axisMouseXPosition =
              e.pageX -
              rect.left; /* ie11 doesn't support boundingRect.x, only .left */
            const t = scaleTime.invert(axisMouseXPosition).getTime();
            setTimelineTooltipState(p => {
              return {
                ...p,
                machemetryId: processId,
                selectedTimelineIdx: timelineIdx,
                modal: {
                  t,
                  clickIndex: timelineIdx,
                },
                selectedProdSegment:
                  // prevents re-rendering TimelineTooltipTagDisplay when mousing over the same segment
                  isEqual(segment, p.selectedProdSegment)
                    ? p.selectedProdSegment
                    : segment,
                // ...(shouldPin ? { isModalPinned: true } : {}),
              };
            });
          } else {
            setTimelineTooltipState(p => {
              return {
                ...p,
                selectedProdSegment: null,
                modal: null,
              };
            });
          }
          return null;
        }}
        onClick={e => {
          unselectAllSegments();

          const context = canvasRef.current?.getContext("2d");
          if (!context) {
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX ? e.clientX - rect.left : 0;
          const segment = findSegment(segments, scaleTime, x);
          if (segment) {
            setTimelineTooltipState(prev => ({
              ...prev,
              selectedProdSegment: segment,
              selectedTimelineIdx: timelineIdx,
              machemetryId: processId,
            }));
            const axisMouseXPosition =
              e.pageX -
              rect.left; /* ie11 doesn't support boundingRect.x, only .left */
            const t = scaleTime.invert(axisMouseXPosition).getTime();
            setTimelineTooltipState(p => {
              return {
                ...p,
                machemetryId: processId,
                modal: {
                  t,
                  clickIndex: timelineIdx,
                },
                selectedProdSegment:
                  // prevents re-rendering TimelineTooltipTagDisplay when mousing over the same segment
                  isEqual(segment, p.selectedProdSegment)
                    ? p.selectedProdSegment
                    : segment,
                isModalPinned: true,
              };
            });
          }
        }}
      >
        <canvas
          ref={canvasRef}
          width={`${width}px`}
          height={`${height}px`}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            cursor: "pointer",
          }}
          id={`timeline-canvas-${processId}`}
        />
      </div>
    </StyledForeignObject>
  );
}

function findSegment(
  segments: types.ProductionSegmentType[],
  scaleTime: d3.ScaleTime<number, number, never>,
  x: number
) {
  return segments.find(segment => {
    const x0 = segment.range.ge;
    const x1 = segment.range.lt;
    const xLeft = x0 ? scaleTime(x0) || 0 : 0;
    const xRight = x1 ? scaleTime(x1) || 0 : 0;
    const isInFuture = x > scaleTime(Date.now());
    return (x >= xLeft || !xLeft) && (x <= xRight || !xRight) && !isInFuture;
  });
}

function getIsRangeWithinHighlightRange(
  timeRange: TimeRangeFinite,
  highlightRange: TimeRangeFinite
) {
  // check if there's any overlap between the two ranges; timeRange can also have null lt and should still return true
  const matchesLt = highlightRange.lt <= timeRange.lt || !timeRange.lt;
  return (
    (highlightRange.ge <= timeRange.ge &&
      timeRange.ge <= highlightRange.lt &&
      matchesLt) ||
    (highlightRange.ge <= timeRange.lt &&
      timeRange.lt <= highlightRange.lt &&
      highlightRange.ge <= timeRange.ge) ||
    (timeRange.ge <= highlightRange.ge &&
      matchesLt &&
      highlightRange.ge <= timeRange.lt &&
      timeRange.ge <= highlightRange.lt)
  );
}
const StyledForeignObject = styled.foreignObject<{ $translateYSafari: number }>`
  ${isSafari
    ? css`
        canvas {
          transform: translate(0, ${props => props.$translateYSafari}px);
          z-index: -1;
          position: relative;
        }
      `
    : ""}
`;

/** offset required for MacOS Safari only, not iPhone Safari */
function getTranslateYSafari(timelineIdx: number, height: number) {
  const isIPhone = getIsIPhone();
  if (isIPhone) {
    return timelineIdx * (height + ROW_PADDING) + 73;
  }
  const isSafari = getIsSafari();
  if (!isSafari) {
    return 0;
  }
  return timelineIdx * (height + ROW_PADDING) + 73; // Significantly increased offset for desktop Safari to fix alignment issue
}
function getIsIPhone() {
  return (
    navigator.userAgent.includes("iPhone") &&
    !navigator.userAgent.includes("Macintosh")
  );
}
